import React from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Progress, Skeleton } from 'antd';
import {
  BankOutlined,
  SettingOutlined,
  DatabaseOutlined,
  UserOutlined,
  CreditCardOutlined,
  CloudUploadOutlined,
  CloudDownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  RightOutlined,
  ToolOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { PageHeader, PageContainer, DevFilePathBadge } from '../../components/common';
import { useUsageMonitor } from '../../hooks/useUsageMonitor';
import { usePermission } from '../../hooks/usePermission';

const { Text } = Typography;

interface SettingsCardProps {
  title: string;
  icon: React.ReactNode;
  path: string;
  children: React.ReactNode;
  loading?: boolean;
}

const SettingsCard: React.FC<SettingsCardProps> = ({ title, icon, path, children, loading }) => {
  const navigate = useNavigate();

  return (
    <Card
      hoverable
      onClick={() => navigate(path)}
      style={{ height: '100%', cursor: 'pointer' }}
      styles={{ body: { padding: 20 } }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space align="center">
            <span style={{ fontSize: 20, color: '#52c41a' }}>{icon}</span>
            <Text strong style={{ fontSize: 15 }}>{title}</Text>
          </Space>
          <RightOutlined style={{ color: '#999' }} />
        </Space>
        {loading ? <Skeleton active paragraph={{ rows: 2 }} /> : children}
      </Space>
    </Card>
  );
};

const SettingsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const enableSubscription = import.meta.env.VITE_ENABLE_SUBSCRIPTION_FEATURES !== 'false';
  const { isAdmin } = usePermission();

  // データ取得
  const { data: company, isLoading: companyLoading } = trpc.company.getCurrentCompany.useQuery();
  const { data: branches, isLoading: branchesLoading } = trpc.branch.list.useQuery(
    { companyId: company?.id ?? '' },
    { enabled: !!company?.id }
  );
  const { data: storageInfo, isLoading: storageLoading } = trpc.storage.getStorageInfo.useQuery(undefined, {
    enabled: !!company?.id,
  });

  // 設備数・プラン上限
  const {
    usage,
    equipmentUtilization,
    isEquipmentNearLimit,
    isLoading: usageLoading,
  } = useUsageMonitor();

  // ストレージ使用率計算
  const storageUsagePercent = storageInfo
    ? Math.round((storageInfo.usedBytes / storageInfo.limitBytes) * 100)
    : 0;
  const storageUsageGB = storageInfo
    ? (storageInfo.usedBytes / (1024 * 1024 * 1024)).toFixed(2)
    : '0';
  const storageLimitGB = storageInfo
    ? (storageInfo.limitBytes / (1024 * 1024 * 1024)).toFixed(0)
    : '0';

  // サブスクリプション状態
  const getSubscriptionStatus = () => {
    if (!company) return { text: '未設定', color: '#999' };
    switch (company.subscriptionStatus) {
      case 'active':
        return { text: '有効', color: '#52c41a' };
      case 'trialing':
        return { text: 'トライアル中', color: '#1890ff' };
      case 'past_due':
        return { text: '支払い遅延', color: '#faad14' };
      case 'canceled':
        return { text: '解約済み', color: '#ff4d4f' };
      default:
        return { text: company.subscriptionStatus || '未設定', color: '#999' };
    }
  };

  const subscriptionStatus = getSubscriptionStatus();

  // 設備使用率計算
  const equipmentUsagePercent = equipmentUtilization ?? 0;

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/settings/SettingsDashboardPage.tsx" />
      <PageHeader
        title="会社設定ダッシュボード"
        subtitle="会社設定の概要を確認できます"
      />

      <Row gutter={[16, 16]}>
        {/* 会社情報 */}
        <Col xs={24} sm={12} lg={8}>
          <SettingsCard
            title="会社情報"
            icon={<BankOutlined />}
            path="/app/settings/company"
            loading={companyLoading}
          >
            {company && (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text type="secondary">会社名</Text>
                <Text>{company.name}</Text>
                {company.address && (
                  <>
                    <Text type="secondary" style={{ marginTop: 8 }}>住所</Text>
                    <Text ellipsis style={{ maxWidth: '100%' }}>
                      {company.address}
                    </Text>
                  </>
                )}
              </Space>
            )}
          </SettingsCard>
        </Col>

        {/* マップ */}
        <Col xs={24} sm={12} lg={8}>
          <SettingsCard
            title="マップ"
            icon={<TeamOutlined />}
            path="/app/settings/branches"
            loading={branchesLoading}
          >
            <Statistic
              title="登録拠点数"
              value={branches?.length ?? 0}
              suffix="拠点"
              valueStyle={{ fontSize: 24 }}
            />
          </SettingsCard>
        </Col>

        {/* システム設定 */}
        <Col xs={24} sm={12} lg={8}>
          <SettingsCard
            title="システム設定"
            icon={<SettingOutlined />}
            path="/app/settings/system-config"
            loading={companyLoading}
          >
            <Space direction="vertical" size={4}>
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text>基本設定完了</Text>
              </Space>
              <Text type="secondary">
                通知設定、表示設定などを管理
              </Text>
            </Space>
          </SettingsCard>
        </Col>

        {/* 設備数（プラン影響） */}
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            onClick={() => navigate('/app/equipments')}
            style={{
              height: '100%',
              cursor: 'pointer',
              borderColor: isEquipmentNearLimit ? '#faad14' : undefined,
              borderWidth: isEquipmentNearLimit ? 2 : 1,
            }}
            styles={{ body: { padding: 20 } }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space align="center">
                  <span style={{ fontSize: 20, color: isEquipmentNearLimit ? '#faad14' : '#1890ff' }}>
                    <ToolOutlined />
                  </span>
                  <Text strong style={{ fontSize: 15 }}>設備登録数</Text>
                  {isEquipmentNearLimit && (
                    <WarningOutlined style={{ color: '#faad14' }} />
                  )}
                </Space>
                <RightOutlined style={{ color: '#999' }} />
              </Space>
              {usageLoading ? (
                <Skeleton active paragraph={{ rows: 2 }} />
              ) : usage ? (
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Progress
                    percent={equipmentUsagePercent}
                    status={isEquipmentNearLimit ? 'exception' : 'normal'}
                    strokeColor={
                      equipmentUsagePercent > 90 ? '#ff4d4f' :
                        equipmentUsagePercent > 70 ? '#faad14' : '#1890ff'
                    }
                  />
                  <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Text style={{ fontSize: 20, fontWeight: 600 }}>
                      {usage.equipmentCount}
                    </Text>
                    <Text type="secondary">
                      / {usage.equipmentLimit === null ? '無制限' : `${usage.equipmentLimit} 台`}
                    </Text>
                  </Space>
                  {isEquipmentNearLimit && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                      上限に近づいています。プランのアップグレードをご検討ください。
                    </Text>
                  )}
                </Space>
              ) : (
                <Text type="secondary">データを取得できません</Text>
              )}
            </Space>
          </Card>
        </Col>

        {/* ストレージ管理 */}
        <Col xs={24} sm={12} lg={8}>
          <SettingsCard
            title="ストレージ管理"
            icon={<DatabaseOutlined />}
            path="/app/settings/storage"
            loading={storageLoading}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Progress
                percent={storageUsagePercent}
                status={storageUsagePercent > 90 ? 'exception' : storageUsagePercent > 70 ? 'active' : 'normal'}
                strokeColor={storageUsagePercent > 90 ? '#ff4d4f' : storageUsagePercent > 70 ? '#faad14' : '#52c41a'}
              />
              <Text type="secondary">
                {storageUsageGB} GB / {storageLimitGB} GB 使用中
              </Text>
            </Space>
          </SettingsCard>
        </Col>

        {/* ユーザー管理 */}
        <Col xs={24} sm={12} lg={8}>
          <SettingsCard
            title="ユーザー管理"
            icon={<UserOutlined />}
            path="/app/settings/users"
            loading={usageLoading}
          >
            <Statistic
              title="アクティブユーザー数"
              value={usage?.activeUserCount ?? 0}
              suffix="人"
              valueStyle={{ fontSize: 24 }}
            />
          </SettingsCard>
        </Col>

        {/* 契約プラン */}
        {enableSubscription && (
          <Col xs={24} sm={12} lg={8}>
            <SettingsCard
              title="契約プラン"
              icon={<CreditCardOutlined />}
              path="/app/settings/subscription"
              loading={companyLoading}
            >
              <Space direction="vertical" size={4}>
                <Space>
                  {subscriptionStatus.text === '有効' ? (
                    <CheckCircleOutlined style={{ color: subscriptionStatus.color }} />
                  ) : (
                    <WarningOutlined style={{ color: subscriptionStatus.color }} />
                  )}
                  <Text style={{ color: subscriptionStatus.color, fontWeight: 500 }}>
                    {subscriptionStatus.text}
                  </Text>
                </Space>
                {company?.subscriptionPlan && (
                  <Text type="secondary">
                    プラン: {company.subscriptionPlan}
                  </Text>
                )}
              </Space>
            </SettingsCard>
          </Col>
        )}

        {/* データインポート */}
        <Col xs={24} sm={12} lg={8}>
          <SettingsCard
            title="データインポート"
            icon={<CloudUploadOutlined />}
            path="/app/settings/import"
            loading={false}
          >
            <Space direction="vertical" size={4}>
              <Text type="secondary">
                Excelファイルから設備、エリア、部品などのデータを一括インポート
              </Text>
            </Space>
          </SettingsCard>
        </Col>

        {/* データエクスポート（管理者のみ） */}
        {isAdmin && (
          <Col xs={24} sm={12} lg={8}>
            <SettingsCard
              title="データエクスポート"
              icon={<CloudDownloadOutlined />}
              path="/app/settings/export"
              loading={false}
            >
              <Space direction="vertical" size={4}>
                <Text type="secondary">
                  設備、作業履歴などのデータをExcel形式でエクスポート
                </Text>
              </Space>
            </SettingsCard>
          </Col>
        )}
      </Row>
    </PageContainer>
  );
};

export default SettingsDashboardPage;
